"use client";

import { c as _c } from "react/compiler-runtime";
import { type ColumnDef, type Header, type OnChangeFn, type PaginationState, type SortingState, columnSizingFeature, columnVisibilityFeature, createColumnHelper, createPaginatedRowModel, createSortedRowModel, flexRender, rowPaginationFeature, rowSortingFeature, tableFeatures, useTable } from "@tanstack/react-table";
import { RefreshCw01, SearchLg, Share03 } from "@untitledui/icons";
import React, { useCallback, useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { PaginationCardDefault } from "@/components/application/pagination/pagination";
import { TableCard } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { usePrefersTapTooltips } from "@/hooks/use-prefers-tap-tooltips";
import { useHasFeatureAccess } from "@/hooks/useHasFeatureAccess";
import { computeTurnover, formatTurnover } from "@/lib/pools/vol-tvl";
import { capturePosthogEvent } from "@/lib/posthog-browser";
import { useAnalysisSidebar } from "@/providers/analysis-sidebar-provider";
import { useAuth } from "@/providers/auth";
import { useDebugSettings } from "@/providers/debug-settings";
import { cx } from "@/utils/cx";
import type { Pool } from "../../data";
import { useFavorites, usePools, usePoolsFilters } from "../../hooks";
import { usePoolsTablePreferences } from "../../hooks/usePoolsTablePreferences";
import { defaultFilters } from "../../store/poolsStore";
import { getCoinGeckoProfileUrl } from "../../utils/coingecko";
import { hasActivePoolFilters } from "../../utils/network-filter";
import { getNetworkBadgeColor } from "../../utils/networkBadgeColor";
import { formatProtocolBadgeLabel, getProtocolBadgeColor } from "../../utils/protocolBadge";
import { getUniswapPoolUrl } from "../../utils/uniswap";
import { FavoriteStar } from "../FavoriteStar";
import { PoolAddressHoverCard } from "../PoolAddressHoverCard";
import { TokenIcon } from "../TokenIcon";
import { AnalyzeButton } from "./AnalyzeButton";
import { VOL_TVL_COLUMN_IDS, fallbackSortColumnId, firstVisibleLockedColumnId, hiddenIdsToVisibility } from "./column-visibility";
import { ColumnsDialog } from "./ColumnsDialog";
import { LockedInsightsGate } from "./LockedInsightsGate";
import { PoolCard, PoolCardSkeleton } from "./PoolCard";
import { PoolsEmptyState, resolvePoolsEmptyVariant } from "./PoolsEmptyState";
import { PoolsExport } from "./PoolsExport";
import { APR_1D_TOOLTIP, APR_7D_TOOLTIP, APR_30D_TOOLTIP, VOL_TVL_1D_TOOLTIP, VOL_TVL_7D_TOOLTIP, VOL_TVL_30D_TOOLTIP, buildIndexes, clampTokenSymbol, enhancePools, filterPoolsOptimized, formatApr, formatTokenSymbol, formatTvl, parseApr, parseFeeTier } from "./PoolsTableShared";
import { SortDirectionGlyph } from "./SortDirectionGlyph";
const ITEMS_PER_PAGE = 10;
/** Matches Tailwind `xl:` — card grid is shown below this width. */
const MOBILE_CARDS_MAX_WIDTH_QUERY = "(max-width: 1279px)";
function subscribeMobileCardsLayout(onChange: () => void) {
  const mq = window.matchMedia(MOBILE_CARDS_MAX_WIDTH_QUERY);
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
function getMobileCardsLayoutSnapshot() {
  return window.matchMedia(MOBILE_CARDS_MAX_WIDTH_QUERY).matches;
}

/** SSR/desktop default: table layout (not cards). */
function getMobileCardsLayoutServerSnapshot() {
  return false;
}
function useShowMobileCardsLayout() {
  return useSyncExternalStore(subscribeMobileCardsLayout, getMobileCardsLayoutSnapshot, getMobileCardsLayoutServerSnapshot);
}
const SORTED_COLUMN_HEADER_CLASS = "bg-brand-100 dark:bg-brand-900";
const SORTED_COLUMN_CELL_CLASS = "bg-brand-50 group-hover/row:bg-brand-50 dark:bg-brand-900/80 dark:group-hover/row:bg-brand-900/80";
const poolsTableFeatures = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  rowPaginationFeature,
  paginatedRowModel: createPaginatedRowModel(),
  columnSizingFeature,
  columnVisibilityFeature
});
type PoolsTableColumnDef = ColumnDef<typeof poolsTableFeatures, Pool>;
function countPoolCardColumnsFromFirstRow(grid: HTMLElement): number {
  const articles = grid.querySelectorAll<HTMLElement>(":scope > article");
  if (articles.length === 0) return 1;
  const firstTop = articles[0].getBoundingClientRect().top;
  let count = 0;
  for (const article of articles) {
    if (Math.abs(article.getBoundingClientRect().top - firstTop) < 8) count++;else break;
  }
  return Math.max(1, count);
}
function usePoolCardGridColumnCount(gridRef, enabled, paginatedLength) {
  const $ = _c(6);
  const [columns, setColumns] = useState(1);
  let t0;
  if ($[0] !== enabled || $[1] !== gridRef) {
    t0 = () => {
      if (!enabled) {
        setColumns(1);
        return;
      }
      const grid = gridRef.current;
      if (!grid) {
        return;
      }
      const measure = () => {
        setColumns(countPoolCardColumnsFromFirstRow(grid));
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(grid);
      return () => ro.disconnect();
    };
    $[0] = enabled;
    $[1] = gridRef;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== enabled || $[4] !== paginatedLength) {
    t1 = [enabled, paginatedLength];
    $[3] = enabled;
    $[4] = paginatedLength;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  useLayoutEffect(t0, t1);
  return columns;
}
function shouldInsertMobileLockedGateAfterCard(index: number, total: number, cardsBeforeGate: number): boolean {
  if (total === 0) return false;
  if (total >= cardsBeforeGate) {
    return index === cardsBeforeGate - 1;
  }
  return index === total - 1;
}

// Stable empty array reference for filteredData memoization
const EMPTY_POOLS_ARRAY: Pool[] = [];
interface PoolsTableProps {
  overridePoolsData?: {
    pools: Pool[];
    total: number;
    userPlan: string;
    features: {
      has7DayPerformance: boolean;
      has30DayPerformance: boolean;
      canSortTvl: boolean;
      canSortApr7d: boolean;
      canSortApr30d: boolean;
      hasCsvDataDownloads: boolean;
    };
  };
  hideFilters?: boolean;
  enableUnfavoritingAnimation?: boolean;
  /** Favorites page only: text filter (token / address); ignores main pools URL filters */
  favoritesSearch?: {
    value: string;
    onChange: (value: string) => void;
  };
}
interface LockedPanelMetrics {
  top: number;
  left: number;
  width: number;
  height: number;
}
const columnHelper = createColumnHelper<typeof poolsTableFeatures, Pool>();

// Filler row component - memoized to prevent unnecessary re-renders
const FillerRow = React.memo<{
  hasFavoritesAccess: boolean;
  hasAccessToPremiumFeatures: boolean;
  volTvlEnabled: boolean;
}>(t0 => {
  const $ = _c(18);
  const {
    hasFavoritesAccess,
    hasAccessToPremiumFeatures,
    volTvlEnabled
  } = t0;
  let t1;
  if ($[0] !== hasFavoritesAccess) {
    t1 = hasFavoritesAccess && <td className="relative min-w-10 shrink-0 py-4 pr-3 pl-4 text-sm font-normal text-tertiary"><div className="mx-auto size-4 rounded bg-quaternary/50" /></td>;
    $[0] = hasFavoritesAccess;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <td className="relative px-3 py-4 text-sm font-normal text-text-tertiary"><div className="h-4 w-5 rounded bg-quaternary/50" /></td>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <td className="relative px-6 py-4 text-sm font-normal text-tertiary"><div className="flex items-center gap-2"><div className="size-5 shrink-0 rounded-full bg-quaternary/50" /><div className="h-4 w-8 rounded bg-quaternary/50" /></div></td>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary"><div className="flex items-center gap-2"><div className="size-5 shrink-0 rounded-full bg-quaternary/50" /><div className="h-4 w-8 rounded bg-quaternary/50" /></div></td>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell"><div className="h-4 w-6 rounded bg-quaternary/50" /></td>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell"><div className="h-4 w-16 rounded bg-quaternary/50" /></td>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell"><div className="h-4 w-6 rounded bg-quaternary/50" /></td>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell"><div className="h-4 w-14 rounded bg-quaternary/50" /></td>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell"><div className="h-4 w-10 rounded bg-quaternary/50" /></td>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== volTvlEnabled) {
    t10 = volTvlEnabled ? <td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell"><div className="h-4 w-12 rounded bg-quaternary/50" /></td> : null;
    $[10] = volTvlEnabled;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] !== hasAccessToPremiumFeatures) {
    t11 = hasAccessToPremiumFeatures ? <><td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell"><div className="h-4 w-10 rounded bg-quaternary/50" /></td><td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell"><div className="h-4 w-10 rounded bg-quaternary/50" /></td><td className="relative px-6 py-4 text-sm font-normal text-tertiary"><div className="h-8 w-26 rounded bg-quaternary/50" /></td></> : <><td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell"><div className="h-4 w-10 rounded bg-quaternary/50" /></td><td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell"><div className="h-4 w-10 rounded bg-quaternary/50" /></td><td className="relative px-6 py-4 text-sm font-normal text-tertiary"><div className="h-8 w-26 rounded bg-quaternary/50" /></td></>;
    $[12] = hasAccessToPremiumFeatures;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t1 || $[15] !== t10 || $[16] !== t11) {
    t12 = <tr className="pointer-events-none h-18 text-sm text-primary">{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}</tr>;
    $[14] = t1;
    $[15] = t10;
    $[16] = t11;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  return t12;
});
FillerRow.displayName = "FillerRow";

/** Distributes TanStack pixel `size` values across 100% of the table width (avoids dead space on wide viewports). */
function compareNullableNumber(a: number | null, b: number | null) {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  return a - b;
}
function columnSizeAsTablePercent(size: number, totalSize: number) {
  if (totalSize <= 0) return undefined;
  return `${size / totalSize * 100}%`;
}
const APR_COLUMN_IDS = new Set(["apr1d", "apr7d", "apr30d"]);
const RIGHT_ALIGNED_COLUMN_IDS = new Set(["feeTier", "tvl", ...VOL_TVL_COLUMN_IDS, ...APR_COLUMN_IDS]);
const TABULAR_NUMS_COLUMN_IDS = new Set(["feeTier", "tvl", ...VOL_TVL_COLUMN_IDS, ...APR_COLUMN_IDS]);
const TOKEN_COLUMN_IDS = new Set(["token1", "token2"]);
const COMPACT_PADDING_COLUMN_IDS = new Set(["uniswap", "network", "feeTier"]);
function getTableHeadHorizontalPadding(columnId: string) {
  if (columnId === "favorite") return "pr-3 pl-4";
  if (columnId === "name") return "px-3";
  if (columnId === "actions") return "pr-4 pl-3";
  if (TOKEN_COLUMN_IDS.has(columnId) || COMPACT_PADDING_COLUMN_IDS.has(columnId)) return "px-4";
  return "px-6";
}
function getTableCellHorizontalPadding(columnId: string) {
  if (columnId === "favorite") return "py-4 pr-3 pl-4";
  if (columnId === "name") return "px-3 py-4";
  if (columnId === "actions") return "py-4 pr-4 pl-3";
  if (TOKEN_COLUMN_IDS.has(columnId) || COMPACT_PADDING_COLUMN_IDS.has(columnId)) return "px-4 py-4";
  return "px-6 py-4";
}
const ColumnHeaderLabel = ({
  label,
  period
}: {
  label: string;
  period?: string;
}) => period ? `${label} ${period}` : label;
type PoolsColumnMeta = {
  tooltip?: string;
};

// Memoized header cell component to prevent unnecessary re-renders
const PREMIUM_BLUR_COLUMN_IDS = new Set(["apr7d", "volTvl7d", "apr30d", "volTvl30d", "actions"]);
const HeaderCell = React.memo<{
  header: Header<typeof poolsTableFeatures, Pool>;
  sortDirection: false | "asc" | "desc";
  isSortedColumn: boolean;
  lockedColumnStartId: string | null;
}>(t0 => {
  const $ = _c(27);
  const {
    header,
    sortDirection,
    isSortedColumn,
    lockedColumnStartId
  } = t0;
  const isSortable = header.column.getCanSort();
  const isLockedColumnStart = header.column.id === lockedColumnStartId;
  const isRightAlignedColumn = RIGHT_ALIGNED_COLUMN_IDS.has(header.id || "");
  let t1;
  if ($[0] !== header) {
    t1 = header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext());
    $[0] = header;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const headerLabel = t1;
  const headerLabelClassName = cx("text-primary-900 text-xs font-bold whitespace-nowrap", isSortable && sortDirection && "font-extrabold text-fg-primary underline underline-offset-3", isSortable && "group-hover/table-head:underline group-hover/table-head:underline-offset-3");
  const sortIcon = isSortable ? <SortDirectionGlyph direction={sortDirection} /> : null;
  const tooltip = (header.column.columnDef.meta as PoolsColumnMeta | undefined)?.tooltip;
  const prefersTapTooltips = usePrefersTapTooltips();
  let t2;
  if ($[2] !== header.column) {
    t2 = header.column.getToggleSortingHandler();
    $[2] = header.column;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const toggleSort = t2;
  const controlClassName = cx("flex h-full w-full items-center py-2 outline-hidden", getTableHeadHorizontalPadding(header.id || ""), isRightAlignedColumn ? "justify-end text-right" : "text-left", isSortable && "cursor-pointer");
  const t3 = cx("flex w-full items-center gap-1", isRightAlignedColumn && "justify-end");
  let t4;
  if ($[4] !== headerLabel || $[5] !== headerLabelClassName) {
    t4 = <span className={headerLabelClassName}>{headerLabel}</span>;
    $[4] = headerLabel;
    $[5] = headerLabelClassName;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== sortIcon || $[8] !== t3 || $[9] !== t4) {
    t5 = <span className={t3}>{t4}{sortIcon}</span>;
    $[7] = sortIcon;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const labelAndGlyph = t5;
  let t6;
  if ($[11] !== controlClassName || $[12] !== header.column || $[13] !== isSortable || $[14] !== labelAndGlyph || $[15] !== prefersTapTooltips || $[16] !== toggleSort || $[17] !== tooltip) {
    t6 = isSortable ? tooltip ? <Tooltip title={tooltip} placement="top" isDisabled={prefersTapTooltips}><TooltipTrigger className={controlClassName} onPress={() => header.column.toggleSorting()}>{labelAndGlyph}</TooltipTrigger></Tooltip> : <button type="button" className={controlClassName} onClick={toggleSort}>{labelAndGlyph}</button> : tooltip ? <Tooltip title={tooltip} placement="top" isDisabled={prefersTapTooltips}><TooltipTrigger className={controlClassName}>{labelAndGlyph}</TooltipTrigger></Tooltip> : <div className={controlClassName}>{labelAndGlyph}</div>;
    $[11] = controlClassName;
    $[12] = header.column;
    $[13] = isSortable;
    $[14] = labelAndGlyph;
    $[15] = prefersTapTooltips;
    $[16] = toggleSort;
    $[17] = tooltip;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const sortControl = t6;
  const t7 = header.column.id;
  const t8 = isLockedColumnStart ? "true" : undefined;
  const t9 = header.id === "favorite" && "min-w-10 shrink-0";
  const t10 = isSortedColumn && SORTED_COLUMN_HEADER_CLASS;
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = cx("group/table-head relative p-0 outline-hidden focus-visible:z-1 focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-offset-bg-primary focus-visible:ring-inset", t9, t10);
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== header.column.id || $[23] !== sortControl || $[24] !== t11 || $[25] !== t8) {
    t12 = <th data-column-id={t7} data-locked-column-start={t8} className={t11}>{sortControl}</th>;
    $[22] = header.column.id;
    $[23] = sortControl;
    $[24] = t11;
    $[25] = t8;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
});
HeaderCell.displayName = "HeaderCell";

// Stable cell renderer functions - memoized to prevent recreation
// Using any for cell context to avoid complex type extraction
const createCellRenderers = () => {
  // Simple text cell renderers (no component state needed)
  const renderTextCell = (info: any, className?: string) => <div className={cx("font-semibold whitespace-nowrap text-text-tertiary", className)}>
			{info.getValue()}
		</div>;
  const renderTokenCell = (info: any) => {
    const symbol = info.getValue() as string;
    const display = formatTokenSymbol(symbol);
    const label = clampTokenSymbol(display);
    const url = getCoinGeckoProfileUrl(info.row.original.coinGeckoDisplayToken1Id);
    return <div className="flex min-w-0 items-center gap-2 font-semibold text-text-tertiary">
				<TokenIcon symbol={symbol} size="sm" showSymbol={false} className="shrink-0" />
				{url ? <a href={url} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate underline underline-offset-2 hover:text-brand-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring" title={display}>
						{label}
					</a> : <span className="min-w-0 truncate" title={display}>
						{label}
					</span>}
			</div>;
  };
  const renderToken2Cell = (info: any) => {
    const symbol = info.getValue() as string;
    const display = formatTokenSymbol(symbol);
    const label = clampTokenSymbol(display);
    const url = getCoinGeckoProfileUrl(info.row.original.coinGeckoDisplayToken2Id);
    return <div className="flex min-w-0 items-center gap-2 font-semibold text-text-tertiary">
				<TokenIcon symbol={symbol} size="sm" showSymbol={false} className="shrink-0" />
				{url ? <a href={url} target="_blank" rel="noopener noreferrer" className="min-w-0 truncate underline underline-offset-2 hover:text-brand-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring" title={display}>
						{label}
					</a> : <span className="min-w-0 truncate" title={display}>
						{label}
					</span>}
			</div>;
  };
  const renderNetworkCell = (info: any) => <div className="md:hidden xl:table-cell">
			<Badge type="color" size="sm" color={getNetworkBadgeColor(info.getValue())}>
				{info.getValue()}
			</Badge>
		</div>;
  const renderProtocolCell = (info: any) => <div className="md:hidden xl:table-cell">
			<Badge type="color" size="sm" color={getProtocolBadgeColor(info.getValue())}>
				{formatProtocolBadgeLabel(info.getValue())}
			</Badge>
		</div>;
  const renderTvlCell = (info: any) => {
    const tvl = info.getValue();
    return <div className="w-full text-right font-semibold whitespace-nowrap">{formatTvl(tvl)}</div>;
  };
  const renderVolTvlCell = (info: any) => {
    const turnover = info.getValue() as number | null;
    return <div className="w-full text-right font-semibold whitespace-nowrap">
				{formatTurnover(turnover)}
			</div>;
  };
  const renderAprCell = (info: any) => <div className="w-full text-right font-semibold whitespace-nowrap">
			{formatApr(info.getValue())}
		</div>;
  const renderPoolCell = (info: any, network: string) => {
    const poolAddress = info.getValue();
    return <div className="font-semibold text-text-tertiary">
				<PoolAddressHoverCard poolAddress={poolAddress} network={network}>
					<a href={getUniswapPoolUrl(network, poolAddress)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded p-1 underline underline-offset-2 hover:text-brand-secondary">
						<Share03 className="size-4" />
					</a>
				</PoolAddressHoverCard>
			</div>;
  };
  return {
    renderTextCell,
    renderTokenCell,
    renderToken2Cell,
    renderProtocolCell,
    renderNetworkCell,
    renderTvlCell,
    renderVolTvlCell,
    renderAprCell,
    renderPoolCell
  };
};
const cellRenderers = createCellRenderers();
const Table = ({
  overridePoolsData,
  hideFilters = false,
  enableUnfavoritingAnimation = false,
  favoritesSearch
}: PoolsTableProps = {}) => {
  // Subscribe to both filters and selectedCoinPair - this component needs both
  const {
    filters,
    selectedCoinPair,
    page,
    setPage,
    resetFilters
  } = usePoolsFilters();
  const {
    isLoggedIn
  } = useAuth();
  const {
    volTvlEnabled
  } = useDebugSettings();
  const {
    openAnalysis
  } = useAnalysisSidebar();
  const hasFavoritesAccess = useHasFeatureAccess("favorites");
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const mobileGridRef = useRef<HTMLDivElement>(null);
  const mobileResultsRef = useRef<HTMLDivElement>(null);
  const [lockedPanelMetrics, setLockedPanelMetrics] = useState<LockedPanelMetrics | null>(null);
  const showMobileCards = useShowMobileCardsLayout();

  // Track pools being unfavorited for opacity animation (only in favorites view)
  // Map: poolAddress -> originalIndex
  const [unfavoritingPools, setUnfavoritingPools] = useState<Map<string, number>>(new Map());
  const currentPoolsRef = useRef<Pool[]>([]);

  // Fetch pools data using React Query, or use override
  const {
    data: fetchedPoolsData,
    isLoading,
    error,
    refetch,
    isFetching
  } = usePools();
  const showLoadingState = isLoading && !fetchedPoolsData;

  // Fetch favorites
  const {
    data: favoritesData = []
  } = useFavorites();
  const favoritedPoolAddresses = useMemo(() => new Set(favoritesData.map(poolAddress => poolAddress.toLowerCase())), [favoritesData]);

  // Extend poolsData to include unfavoriting pools at their original positions until animation completes
  const poolsData = useMemo(() => {
    const baseData = overridePoolsData || fetchedPoolsData;
    const currentPools = baseData?.pools || [];

    // Always update the ref with current pools
    currentPoolsRef.current = currentPools;
    if (!baseData || !enableUnfavoritingAnimation || unfavoritingPools.size === 0) {
      return baseData;
    }
    const currentPoolAddresses = new Set(currentPools.map(p => p.pool));

    // Create map once for O(1) lookups instead of array.find
    const poolMap = fetchedPoolsData?.pools ? new Map(fetchedPoolsData.pools.map(p_0 => [p_0.pool, p_0])) : null;

    // Find unfavoriting pools that need to be reinserted
    const poolsToInsert: Array<{
      pool: Pool;
      index: number;
    }> = [];
    unfavoritingPools.forEach((originalIndex, poolAddress_0) => {
      if (!currentPoolAddresses.has(poolAddress_0) && poolMap) {
        const pool = poolMap.get(poolAddress_0); // O(1) lookup instead of O(n) find
        if (pool) {
          poolsToInsert.push({
            pool,
            index: originalIndex
          });
        }
      }
    });
    if (poolsToInsert.length > 0) {
      // Sort by original index to insert in correct order
      poolsToInsert.sort((a, b) => a.index - b.index);

      // Create new array with unfavoriting pools inserted at their original positions
      const newPools: Pool[] = [...currentPools];
      poolsToInsert.forEach(({
        pool: pool_0,
        index
      }) => {
        // Insert at the original index, but clamp to valid range
        const insertIndex = Math.min(index, newPools.length);
        newPools.splice(insertIndex, 0, pool_0);
      });
      return {
        ...baseData,
        pools: newPools,
        total: newPools.length
      };
    }
    return baseData;
  }, [overridePoolsData, fetchedPoolsData, enableUnfavoritingAnimation, unfavoritingPools]);

  // Callback to track when a pool is being unfavorited (only when animation is enabled)
  const handleUnfavoriting = useMemo(() => {
    if (!enableUnfavoritingAnimation) return undefined;
    return (poolAddress_1: string) => {
      // Find the original index of this pool before it gets removed
      // Use the ref which is always up-to-date with the current rendered pools
      const currentPools_0 = currentPoolsRef.current;
      const originalIndex_0 = currentPools_0.findIndex(p_1 => p_1.pool === poolAddress_1);
      if (originalIndex_0 !== -1) {
        setUnfavoritingPools(prev => {
          const next = new Map(prev);
          next.set(poolAddress_1, originalIndex_0);
          return next;
        });

        // Remove from map after animation completes
        setTimeout(() => {
          setUnfavoritingPools(prev_0 => {
            const next_0 = new Map(prev_0);
            next_0.delete(poolAddress_1);
            return next_0;
          });
        }, 800); // Match animation duration
      }
    };
  }, [enableUnfavoritingAnimation]);

  // Get user's feature access from API response (database-backed)
  const has7DayPerformance = poolsData?.features.has7DayPerformance ?? false;
  const has30DayPerformance = poolsData?.features.has30DayPerformance ?? false;
  const hasAccessToPremiumFeatures = has7DayPerformance || has30DayPerformance;
  const canSortTvl = poolsData?.features.canSortTvl ?? false;
  const canSortApr7d = poolsData?.features.canSortApr7d ?? false;
  const canSortApr30d = poolsData?.features.canSortApr30d ?? false;
  const hasCsvDataDownloads = poolsData?.features.hasCsvDataDownloads ?? false;

  // Defer filtering to allow UI to update immediately
  // The visual selection updates instantly, but filtering happens in background
  const deferredFilters = useDeferredValue(filters);
  const deferredSelectedCoinPair = useDeferredValue(selectedCoinPair);
  const deferredFavoritesKeyword = useDeferredValue(favoritesSearch?.value ?? "");
  const filtersForRows = useMemo(() => {
    const next_1 = hideFilters ? {
      ...defaultFilters,
      keyword: deferredFavoritesKeyword
    } : deferredFilters;
    if (volTvlEnabled || next_1.volTvl === "all") return next_1;
    return {
      ...next_1,
      volTvl: "all"
    };
  }, [hideFilters, deferredFavoritesKeyword, deferredFilters, volTvlEnabled]);
  const selectedCoinPairForRows = hideFilters ? null : deferredSelectedCoinPair;

  // Memoize enhanced pools with pre-computed searchable text (only rebuilds when pools data changes)
  const enhancedPools = useMemo(() => {
    if (!poolsData?.pools || poolsData.pools.length === 0) return [];
    return enhancePools(poolsData.pools);
  }, [poolsData?.pools]);

  // Memoize indexes (only rebuilds when enhanced pools change)
  const poolIndexes = useMemo(() => {
    if (enhancedPools.length === 0) return null;
    return buildIndexes(enhancedPools);
  }, [enhancedPools]);

  // Filter with deferred values using optimized filter function with indexes
  // Use stable empty array reference to prevent unnecessary re-renders
  const filteredData = useMemo(() => {
    if (!enhancedPools.length || !poolIndexes) return EMPTY_POOLS_ARRAY;
    return filterPoolsOptimized(enhancedPools, poolIndexes, filtersForRows, selectedCoinPairForRows);
  }, [enhancedPools, poolIndexes, filtersForRows, selectedCoinPairForRows]);
  const canEditColumns = hasAccessToPremiumFeatures && volTvlEnabled;
  const {
    hiddenColumnIds: storedHiddenColumnIds,
    saveStatus,
    setColumnVisible,
    resetColumns
  } = usePoolsTablePreferences(canEditColumns);
  const hiddenColumnIds = useMemo(() => volTvlEnabled ? storedHiddenColumnIds : [...storedHiddenColumnIds, ...VOL_TVL_COLUMN_IDS], [storedHiddenColumnIds, volTvlEnabled]);
  const columnVisibility = useMemo(() => hiddenIdsToVisibility(hiddenColumnIds), [hiddenColumnIds]);
  const lockedColumnStartId = firstVisibleLockedColumnId(hiddenColumnIds);

  // Sorting state
  const [sorting, setSorting] = useState<SortingState>([{
    id: "apr1d",
    desc: true
  }]);
  const activeSortedColumnId = sorting[0]?.id;
  useEffect(() => {
    const active = sorting[0]?.id;
    if (!active || !hiddenColumnIds.includes(active)) return;
    const nextId = fallbackSortColumnId(hiddenColumnIds);
    if (nextId === active) return;
    setSorting([{
      id: nextId,
      desc: true
    }]);
  }, [hiddenColumnIds, sorting]);

  // Pagination state (1-based page lives in URL via usePoolsFilters)
  const pagination = useMemo<PaginationState>(() => ({
    pageIndex: page - 1,
    pageSize: ITEMS_PER_PAGE
  }), [page]);
  const onPaginationChange = useCallback<OnChangeFn<PaginationState>>(updater => {
    const nextPagination = typeof updater === "function" ? updater(pagination) : updater;
    setPage(nextPagination.pageIndex + 1);
  }, [pagination, setPage]);

  // Memoized cell renderers that need component state
  const renderFavoriteCell = React.useCallback(({
    row
  }: {
    row: {
      original: Pool;
    };
  }) => <FavoriteStar poolAddress={row.original.pool} network={row.original.network} isFavorited={favoritedPoolAddresses.has(row.original.pool.toLowerCase())} onUnfavoriting={handleUnfavoriting} />, [favoritedPoolAddresses, handleUnfavoriting]);
  const renderPoolCellWithState = React.useCallback((info: any): React.JSX.Element => cellRenderers.renderPoolCell(info, info.row.original.network), []);
  const renderTvlCellWithState = React.useCallback((info_0: any): React.JSX.Element => cellRenderers.renderTvlCell(info_0), []);
  const renderVolTvlCellWithState = React.useCallback((info_1: any): React.JSX.Element => cellRenderers.renderVolTvlCell(info_1), []);
  const renderApr7dCell = React.useCallback((info_2: any): React.JSX.Element => {
    if (hasAccessToPremiumFeatures) {
      return <div className="w-full text-right font-semibold whitespace-nowrap">
						{has7DayPerformance && info_2.getValue() ? formatApr(info_2.getValue()) : <span className="inline-block h-4 w-10 rounded bg-quaternary" />}
					</div>;
    }
    return <div className="w-full text-right whitespace-nowrap">
					<span className="inline-block h-4 w-10 rounded bg-quaternary" />
				</div>;
  }, [hasAccessToPremiumFeatures, has7DayPerformance]);
  const renderApr30dCell = React.useCallback((info_3: any): React.JSX.Element => {
    if (hasAccessToPremiumFeatures) {
      return <div className="w-full text-right font-semibold whitespace-nowrap">
						{has30DayPerformance && info_3.getValue() ? formatApr(info_3.getValue()) : <span className="inline-block h-4 w-10 rounded bg-quaternary" />}
					</div>;
    }
    return <div className="w-full text-right whitespace-nowrap">
					<span className="inline-block h-4 w-10 rounded bg-quaternary" />
				</div>;
  }, [hasAccessToPremiumFeatures, has30DayPerformance]);
  const renderActionsCell = React.useCallback(({
    row: row_0
  }: {
    row: {
      original: Pool;
    };
  }) => {
    if (hasAccessToPremiumFeatures) {
      return <AnalyzeButton onClick={() => openAnalysis(row_0.original.pool, row_0.original.network)} />;
    }
    return <div className="flex justify-end md:hidden xl:flex">
					<div className="h-8 w-26 rounded bg-quaternary" />
				</div>;
  }, [hasAccessToPremiumFeatures, openAnalysis]);

  // Create column definitions
  // Sort state is local to the table and composes with the URL-backed filter state.
  const columns = useMemo(() => {
    const cols: PoolsTableColumnDef[] = [];

    // Favorite column - NOT sortable
    if (hasFavoritesAccess) {
      cols.push(columnHelper.display({
        id: "favorite",
        header: "",
        size: 48,
        enableSorting: false,
        enableHiding: false,
        cell: renderFavoriteCell
      }) as PoolsTableColumnDef);
    }

    // Pool column - NOT sortable
    cols.push(columnHelper.accessor("pool", {
      id: "name",
      header: "Pool",
      size: 46,
      enableSorting: false,
      enableHiding: false,
      cell: renderPoolCellWithState
    }) as PoolsTableColumnDef);

    // Token 1 column
    cols.push(columnHelper.accessor("token1", {
      id: "token1",
      header: "Token 1",
      size: 120,
      enableSorting: true,
      enableHiding: false,
      cell: cellRenderers.renderTokenCell,
      sortFn: (rowA, rowB) => formatTokenSymbol(rowA.original.token1).localeCompare(formatTokenSymbol(rowB.original.token1))
    }) as PoolsTableColumnDef);

    // Token 2 column
    cols.push(columnHelper.accessor("token2", {
      id: "token2",
      header: "Token 2",
      size: 120,
      enableSorting: true,
      enableHiding: false,
      cell: cellRenderers.renderToken2Cell,
      sortFn: (rowA_0, rowB_0) => formatTokenSymbol(rowA_0.original.token2).localeCompare(formatTokenSymbol(rowB_0.original.token2))
    }) as PoolsTableColumnDef);

    // Protocol column
    cols.push(columnHelper.accessor("uniswap", {
      id: "uniswap",
      // Cell content is a 2-char badge (V3/V4); a short header lets the column shrink.
      header: "Ver.",
      size: 64,
      enableSorting: true,
      enableHiding: false,
      cell: cellRenderers.renderProtocolCell,
      sortFn: (rowA_1, rowB_1) => rowA_1.original.uniswap.localeCompare(rowB_1.original.uniswap)
    }) as PoolsTableColumnDef);

    // Network column
    cols.push(columnHelper.accessor("network", {
      id: "network",
      header: "Network",
      size: 96,
      enableSorting: true,
      enableHiding: false,
      cell: cellRenderers.renderNetworkCell,
      sortFn: (rowA_2, rowB_2) => rowA_2.original.network.localeCompare(rowB_2.original.network)
    }) as PoolsTableColumnDef);

    // Fee Tier column
    cols.push(columnHelper.accessor("feeTier", {
      id: "feeTier",
      header: "Fee Tier",
      size: 94,
      enableSorting: true,
      enableHiding: false,
      sortDescFirst: true,
      cell: info_4 => cellRenderers.renderTextCell(info_4, "w-full text-right"),
      sortFn: (rowA_3, rowB_3) => parseFeeTier(rowA_3.original.feeTier) - parseFeeTier(rowB_3.original.feeTier)
    }) as PoolsTableColumnDef);

    // TVL column - SORTABLE (requires premium feature: canSortTvl)
    cols.push(columnHelper.accessor("tvl", {
      id: "tvl",
      header: "TVL",
      size: 114,
      cell: renderTvlCellWithState,
      enableSorting: canSortTvl,
      // Only sortable if user has premium feature
      sortDescFirst: true,
      sortFn: (rowA_4, rowB_4) => rowA_4.original.tvl - rowB_4.original.tvl
    }) as PoolsTableColumnDef);

    // APR 1D column - SORTABLE (always available)
    cols.push(columnHelper.accessor("apr1d", {
      id: "apr1d",
      header: () => <ColumnHeaderLabel label="APR" period="(1D)" />,
      meta: {
        tooltip: APR_1D_TOOLTIP
      },
      size: 118,
      enableSorting: true,
      sortDescFirst: true,
      cell: cellRenderers.renderAprCell,
      sortFn: (rowA_5, rowB_5) => {
        const aValue = parseApr(rowA_5.original.apr1d);
        const bValue = parseApr(rowB_5.original.apr1d);
        return aValue - bValue;
      }
    }) as PoolsTableColumnDef);
    if (volTvlEnabled) {
      cols.push(columnHelper.accessor(row_1 => computeTurnover(row_1.tvl, row_1.volume1d, "1d"), {
        id: "volTvl",
        header: () => <ColumnHeaderLabel label="Vol/TVL" period="(1D)" />,
        meta: {
          tooltip: VOL_TVL_1D_TOOLTIP
        },
        size: 110,
        enableSorting: true,
        sortDescFirst: true,
        cell: renderVolTvlCellWithState,
        sortFn: (rowA_6, rowB_6) => {
          const a_0 = computeTurnover(rowA_6.original.tvl, rowA_6.original.volume1d, "1d");
          const b_0 = computeTurnover(rowB_6.original.tvl, rowB_6.original.volume1d, "1d");
          if (a_0 === null && b_0 === null) return 0;
          if (a_0 === null) return 1;
          if (b_0 === null) return -1;
          return a_0 - b_0;
        }
      }) as PoolsTableColumnDef);
    }

    // APR 7D column (premium) - SORTABLE (requires premium feature: canSortApr7d)
    cols.push(columnHelper.accessor("apr7d", {
      id: "apr7d",
      header: () => <ColumnHeaderLabel label="APR" period="(7D)" />,
      meta: {
        tooltip: APR_7D_TOOLTIP
      },
      size: 118,
      cell: renderApr7dCell,
      enableSorting: canSortApr7d,
      // Only sortable if user has premium feature
      enableHiding: canEditColumns,
      sortDescFirst: true,
      sortFn: (rowA_7, rowB_7) => {
        const aValue_0 = parseApr(rowA_7.original.apr7d);
        const bValue_0 = parseApr(rowB_7.original.apr7d);
        return aValue_0 - bValue_0;
      }
    }) as PoolsTableColumnDef);
    if (volTvlEnabled) {
      cols.push(columnHelper.accessor(row_2 => computeTurnover(row_2.tvl, row_2.volume7d, "7d"), {
        id: "volTvl7d",
        header: () => <ColumnHeaderLabel label="Vol/TVL" period="(7D)" />,
        meta: {
          tooltip: VOL_TVL_7D_TOOLTIP
        },
        size: 110,
        enableSorting: canSortApr7d,
        sortDescFirst: true,
        cell: renderVolTvlCellWithState,
        sortFn: (rowA_8, rowB_8) => compareNullableNumber(computeTurnover(rowA_8.original.tvl, rowA_8.original.volume7d, "7d"), computeTurnover(rowB_8.original.tvl, rowB_8.original.volume7d, "7d"))
      }) as PoolsTableColumnDef);
    }

    // APR 30D column (premium) - SORTABLE (requires premium feature: canSortApr30d)
    cols.push(columnHelper.accessor("apr30d", {
      id: "apr30d",
      header: () => <ColumnHeaderLabel label="APR" period="(30D)" />,
      meta: {
        tooltip: APR_30D_TOOLTIP
      },
      size: 118,
      cell: renderApr30dCell,
      enableSorting: canSortApr30d,
      // Only sortable if user has premium feature
      enableHiding: canEditColumns,
      sortDescFirst: true,
      sortFn: (rowA_9, rowB_9) => {
        const aValue_1 = parseApr(rowA_9.original.apr30d);
        const bValue_1 = parseApr(rowB_9.original.apr30d);
        return aValue_1 - bValue_1;
      }
    }) as PoolsTableColumnDef);
    if (volTvlEnabled) {
      cols.push(columnHelper.accessor(row_3 => computeTurnover(row_3.tvl, row_3.volume30d, "30d"), {
        id: "volTvl30d",
        header: () => <ColumnHeaderLabel label="Vol/TVL" period="(30D)" />,
        meta: {
          tooltip: VOL_TVL_30D_TOOLTIP
        },
        size: 118,
        enableSorting: canSortApr30d,
        sortDescFirst: true,
        cell: renderVolTvlCellWithState,
        sortFn: (rowA_10, rowB_10) => compareNullableNumber(computeTurnover(rowA_10.original.tvl, rowA_10.original.volume30d, "30d"), computeTurnover(rowB_10.original.tvl, rowB_10.original.volume30d, "30d"))
      }) as PoolsTableColumnDef);
    }

    // Actions column (premium) - NOT sortable
    cols.push(columnHelper.display({
      id: "actions",
      header: "Analyze",
      size: 96,
      enableSorting: false,
      enableHiding: false,
      cell: renderActionsCell
    }) as PoolsTableColumnDef);
    return cols;
  }, [hasFavoritesAccess, renderFavoriteCell, renderPoolCellWithState, renderTvlCellWithState, renderVolTvlCellWithState, renderApr7dCell, renderApr30dCell, renderActionsCell, canSortTvl, canSortApr7d, canSortApr30d, canEditColumns, volTvlEnabled]);

  // Create table instance
  const table = useTable({
    features: poolsTableFeatures,
    data: filteredData,
    columns,
    state: {
      sorting,
      pagination,
      columnVisibility
    },
    onPaginationChange,
    enableSortingRemoval: false,
    onSortingChange: updater_0 => {
      const newSorting = typeof updater_0 === "function" ? updater_0(sorting) : updater_0;
      setSorting(newSorting);

      // Track sort change with PostHog (async to prevent blocking UI)
      if (newSorting.length > 0) {
        // Use setTimeout to make PostHog calls async and non-blocking
        setTimeout(() => {
          const sort = newSorting[0];
          const column = columns.find(col => col.id === sort.id);
          const allowsSorting = column && "enableSorting" in column ? column.enableSorting !== false : true;
          if (allowsSorting) {
            void capturePosthogEvent("pool_sort_changed", {
              column: sort.id,
              direction: sort.desc ? "descending" : "ascending",
              requires_premium: false,
              user_has_access: true
            });
          } else {
            // Track feature access denied
            void capturePosthogEvent("feature_access_denied", {
              feature: sort.id === "tvl" ? "canSortTvl" : sort.id === "apr7d" ? "7_day_performance" : "30_day_performance",
              required_plan: "pro",
              context: "sort_column"
            });
          }
        }, 0);
      }
    },
    manualPagination: false,
    autoResetPageIndex: false // We handle page reset manually when filters change
  });
  const scrollMobileResultsIntoView = useCallback(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia(MOBILE_CARDS_MAX_WIDTH_QUERY).matches) return;
    requestAnimationFrame(() => {
      mobileResultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    });
  }, []);
  const handlePaginationPageChange = useCallback((nextPage: number) => {
    const nextIndex = nextPage - 1;
    if (pagination.pageIndex === nextIndex) return;
    table.setPageIndex(nextIndex);
    void capturePosthogEvent("pool_table_paginated", {
      page_number: nextPage,
      items_per_page: ITEMS_PER_PAGE,
      total_pages: table.getPageCount()
    });
    scrollMobileResultsIntoView();
  }, [pagination.pageIndex, scrollMobileResultsIntoView, table]);
  const visibleLeafColumns = table.getVisibleLeafColumns();
  const tableTotalSize = visibleLeafColumns.reduce((sum, column_0) => sum + column_0.getSize(), 0);

  // Clamp page when the filtered dataset shrinks (e.g. deep-linked page=5 with fewer results).
  useEffect(() => {
    if (filteredData.length === 0) return;
    const pageCount = Math.max(1, Math.ceil(filteredData.length / ITEMS_PER_PAGE));
    if (page > pageCount) {
      setPage(pageCount);
    }
  }, [filteredData.length, page, setPage]);
  useEffect(() => {
    if (!hideFilters || !favoritesSearch) return;
    setPage(1);
  }, [favoritesSearch?.value, hideFilters, setPage]);

  // Memoize padded rows calculation to prevent recreation on every render
  const paginatedRows = table.getRowModel().rows;
  useEffect(() => {
    if (hasAccessToPremiumFeatures || isLoading || !tableWrapperRef.current) {
      setLockedPanelMetrics(null);
      return;
    }
    const tableWrapper = tableWrapperRef.current;
    let frameId: number | null = null;
    const updateLockedPanelMetrics = () => {
      const table_0 = tableWrapper.querySelector("table");
      const thead = table_0?.querySelector("thead");
      const tbody = table_0?.querySelector("tbody");
      const lockedColumnStart = table_0?.querySelector<HTMLElement>('thead [data-locked-column-start="true"]') ?? table_0?.querySelector<HTMLElement>('tbody tr:first-child [data-locked-column-start="true"]');
      if (!(table_0 && thead && tbody && lockedColumnStart)) {
        setLockedPanelMetrics(null);
        return;
      }
      const wrapperRect = tableWrapper.getBoundingClientRect();
      const tableRect = table_0.getBoundingClientRect();
      const tbodyRect = tbody.getBoundingClientRect();
      const theadRect = thead.getBoundingClientRect();
      const lockedColumnRect = lockedColumnStart.getBoundingClientRect();
      const nextMetrics = {
        top: theadRect.bottom - wrapperRect.top,
        left: Math.max(0, lockedColumnRect.left - wrapperRect.left + tableWrapper.scrollLeft),
        width: Math.max(0, tableRect.right - lockedColumnRect.left),
        height: tbodyRect.height
      };
      setLockedPanelMetrics(prev_1 => {
        if (prev_1 && prev_1.top === nextMetrics.top && prev_1.left === nextMetrics.left && prev_1.width === nextMetrics.width && prev_1.height === nextMetrics.height) {
          return prev_1;
        }
        return nextMetrics;
      });
    };
    const scheduleMetricsUpdate = () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      frameId = requestAnimationFrame(() => {
        updateLockedPanelMetrics();
        frameId = null;
      });
    };
    scheduleMetricsUpdate();
    const resizeObserver = new ResizeObserver(scheduleMetricsUpdate);
    resizeObserver.observe(tableWrapper);
    const table_1 = tableWrapper.querySelector("table");
    const tbody_0 = table_1?.querySelector("tbody");
    if (table_1) {
      resizeObserver.observe(table_1);
    }
    if (tbody_0) {
      resizeObserver.observe(tbody_0);
    }
    return () => {
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
      resizeObserver.disconnect();
    };
  }, [hasAccessToPremiumFeatures, isLoading, lockedColumnStartId, paginatedRows.length]);
  const paddedRows = useMemo(() => {
    const rows: Array<(typeof paginatedRows)[0] | null> = [...paginatedRows];
    const emptyCount = ITEMS_PER_PAGE - paginatedRows.length;
    for (let i = 0; i < emptyCount; i++) {
      rows.push(null);
    }
    return rows;
  }, [paginatedRows]);
  const mobileGridColumnCount = usePoolCardGridColumnCount(mobileGridRef, showMobileCards && !error, paginatedRows.length);
  if (error) {
    return <TableCard.Root>
				<div role="alert" className="flex min-h-[400px] flex-col items-center justify-center gap-3 px-6 text-center">
					<p className="text-sm font-semibold text-text-primary">Couldn't load pools</p>
					<p className="text-sm text-text-tertiary">Check your connection and try again.</p>
					<Button color="secondary" size="sm" iconLeading={RefreshCw01} isLoading={isFetching} onClick={() => void refetch()}>
						Retry
					</Button>
				</div>
			</TableCard.Root>;
  }
  const shouldShowLockedInsightsGate = !showLoadingState && !hasAccessToPremiumFeatures && !!lockedPanelMetrics && filteredData.length > 0;
  const hasNoPools = paginatedRows.length === 0 && filteredData.length === 0;
  const favoritesSearchTrimmed = favoritesSearch?.value.trim() ?? "";
  const emptyVariant = resolvePoolsEmptyVariant({
    hideFilters,
    favoritesSearch: favoritesSearchTrimmed,
    hasActiveFilters: hasActivePoolFilters(volTvlEnabled ? filters : {
      ...filters,
      volTvl: "all"
    }) || selectedCoinPair !== null
  });
  const totalFavoritedInView = enhancedPools.length;
  const favoritesToolbarSummary = hideFilters && favoritesSearchTrimmed ? `${filteredData.length} of ${totalFavoritedInView} favorites` : `${filteredData.length} ${filteredData.length === 1 ? "favorite" : "favorites"}`;
  const cardsBeforeMobileLockedGate = mobileGridColumnCount === 1 ? 3 : 2 * mobileGridColumnCount;
  const showMobileLockedInsightsGate = !isLoggedIn && !hasAccessToPremiumFeatures && !showLoadingState && !hasNoPools;
  const columnsAction = canEditColumns ? <ColumnsDialog hiddenColumnIds={hiddenColumnIds} saveStatus={saveStatus} onChange={setColumnVisible} onReset={resetColumns} /> : null;
  return <TableCard.Root>
			{!hideFilters && <PoolsExport pools={filteredData} filters={filters} selectedCoinPair={selectedCoinPair} hasCsvDataDownloads={hasCsvDataDownloads} includeVolTvl={volTvlEnabled} isLoading={showLoadingState} toolbarActions={columnsAction} />}
			{hideFilters && <div className="flex flex-col gap-3 border-b border-border-primary px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 md:px-6">
					<div className="shrink-0 text-sm font-semibold text-text-secondary">
						{favoritesToolbarSummary}
					</div>
					<div className="flex min-w-0 items-center gap-2 sm:flex-1 sm:justify-end">
						{favoritesSearch ? <div className="min-w-0 sm:max-w-md sm:flex-1">
								<Input aria-label="Search favorites" icon={SearchLg} placeholder="Search By Token Or Address" size="sm" value={favoritesSearch.value} onChange={favoritesSearch.onChange} />
							</div> : null}
						{columnsAction}
					</div>
				</div>}
			{showMobileCards ? <div ref={mobileResultsRef} className="scroll-mt-4 px-4 py-4 md:px-6">
					{showLoadingState ? <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,360px),1fr))] gap-4">
							{Array.from({
          length: ITEMS_PER_PAGE
        }).map((_, index_0) => <PoolCardSkeleton key={`pool-card-skeleton-${index_0}`} />)}
						</div> : hasNoPools ? <PoolsEmptyState variant={emptyVariant} onClearFilters={resetFilters} className="min-h-[240px] rounded-lg border border-dashed border-border-primary bg-secondary" /> : <div ref={mobileGridRef} className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,360px),1fr))] gap-4">
							{paginatedRows.flatMap((row_4, index_1) => {
          const isUnfavoriting = enableUnfavoritingAnimation && unfavoritingPools.has(row_4.original.pool);
          const card = <PoolCard key={row_4.id} pool={row_4.original} hasFavoritesAccess={hasFavoritesAccess} isFavorited={favoritedPoolAddresses.has(row_4.original.pool.toLowerCase())} isLoggedIn={isLoggedIn} hasAccessToPremiumFeatures={hasAccessToPremiumFeatures} has7DayPerformance={has7DayPerformance} has30DayPerformance={has30DayPerformance} hiddenColumnIds={hiddenColumnIds} isUnfavoriting={isUnfavoriting} onAnalyze={openAnalysis} onUnfavoriting={handleUnfavoriting} />;
          if (!showMobileLockedInsightsGate || !shouldInsertMobileLockedGateAfterCard(index_1, paginatedRows.length, cardsBeforeMobileLockedGate)) {
            return [card];
          }
          return [card, <div key={`${row_4.id}-locked-insights-gate`} className="col-span-full">
										<LockedInsightsGate isLoggedIn={isLoggedIn} className="h-auto min-h-0 rounded-lg border border-border-secondary shadow-xs" />
									</div>];
        })}
						</div>}
				</div> : <div ref={tableWrapperRef} className={cx("relative",
    // Logged-out overlay must not widen scrollable overflow (inner CTA has large min-content width).
    shouldShowLockedInsightsGate ? "overflow-x-hidden" : "overflow-x-auto")}>
					<table className="w-full table-fixed overflow-x-hidden">
						<colgroup>
							{visibleLeafColumns.map(column_1 => <col key={column_1.id} style={{
            width: columnSizeAsTablePercent(column_1.getSize(), tableTotalSize)
          }} />)}
						</colgroup>
						<thead className="relative h-11 bg-secondary">
							<tr className="h-11">
								{table.getHeaderGroups().map(headerGroup => headerGroup.headers.map(header => <HeaderCell key={header.id} header={header} sortDirection={header.column.getIsSorted()} isSortedColumn={header.column.id === activeSortedColumnId} lockedColumnStartId={lockedColumnStartId} />))}
							</tr>
						</thead>
						<tbody>
							{showLoadingState ?
          // Skeleton rows
          Array.from({
            length: ITEMS_PER_PAGE
          }).map((__0, index_2) => {
            const skeletonClass = "animate-pulse bg-quaternary/50";
            return <tr key={`skeleton-${index_2}`} className="pointer-events-none h-18 text-sm text-primary">
												{/* Favorite */}
												{hasFavoritesAccess && <td className="relative min-w-10 shrink-0 py-4 pr-3 pl-4 text-sm font-normal text-tertiary">
														<div className={`mx-auto size-4 rounded ${skeletonClass}`} />
													</td>}
												{/* Pool */}
												<td className="relative px-3 py-4 text-sm font-normal text-text-tertiary">
													<div className={`h-4 w-5 rounded ${skeletonClass}`} />
												</td>
												{/* Token 1 */}
												<td className="relative px-6 py-4 text-sm font-normal text-tertiary">
													<div className="flex items-center gap-2">
														<div className={`size-5 shrink-0 rounded-full ${skeletonClass}`} />
														<div className={`h-4 w-8 rounded ${skeletonClass}`} />
													</div>
												</td>
												{/* Token 2 */}
												<td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary">
													<div className="flex items-center gap-2">
														<div className={`size-5 shrink-0 rounded-full ${skeletonClass}`} />
														<div className={`h-4 w-8 rounded ${skeletonClass}`} />
													</div>
												</td>
												{/* Protocol */}
												<td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell">
													<div className={`h-4 w-6 rounded ${skeletonClass}`} />
												</td>
												{/* Network */}
												<td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell">
													<div className={`h-4 w-16 rounded ${skeletonClass}`} />
												</td>
												{/* Fee Tier */}
												<td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell">
													<div className={`h-4 w-6 rounded ${skeletonClass}`} />
												</td>
												{/* TVL */}
												<td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell">
													<div className={`h-4 w-20 rounded ${skeletonClass}`} />
												</td>
												{/* APR 1D */}
												<td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell">
													<div className={`h-4 w-10 rounded ${skeletonClass}`} />
												</td>
												{volTvlEnabled ? <td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell">
														<div className={`h-4 w-12 rounded ${skeletonClass}`} />
													</td> : null}
												{hasAccessToPremiumFeatures ? <>
														{/* APR 7D */}
														<td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell">
															<div className={`h-4 w-10 rounded ${skeletonClass}`} />
														</td>
														{/* APR 30D */}
														<td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell">
															<div className={`h-4 w-10 rounded ${skeletonClass}`} />
														</td>
														{/* Actions */}
														<td className="relative px-6 py-4 text-sm font-normal text-tertiary">
															<div className={`h-8 w-26 rounded ${skeletonClass}`} />
														</td>
													</> : <>
														{/* APR 7D - skeleton blob */}
														<td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell">
															<div className={`h-4 w-10 rounded ${skeletonClass}`} />
														</td>
														{/* APR 30D - skeleton blob */}
														<td className="relative px-6 py-4 text-sm font-normal whitespace-nowrap text-tertiary md:hidden xl:table-cell">
															<div className={`h-4 w-10 rounded ${skeletonClass}`} />
														</td>
														{/* Actions - skeleton blob */}
														<td className="relative px-6 py-4 text-sm font-normal text-tertiary">
															<div className={`h-8 w-26 rounded ${skeletonClass}`} />
														</td>
													</>}
											</tr>;
          }) : (() => {
            if (hasNoPools) {
              return <tr>
													<td colSpan={visibleLeafColumns.length} className="p-0">
														<PoolsEmptyState variant={emptyVariant} onClearFilters={resetFilters} />
													</td>
												</tr>;
            }
            return paddedRows.map((row_5, index_3) => {
              if (!row_5) {
                // Render filler row using memoized component
                return <FillerRow key={`filler-${index_3}`} hasFavoritesAccess={hasFavoritesAccess} hasAccessToPremiumFeatures={hasAccessToPremiumFeatures} volTvlEnabled={volTvlEnabled} />;
              }
              const isUnfavoriting_0 = enableUnfavoritingAnimation && unfavoritingPools.has(row_5.original.pool);
              return <tr key={row_5.id} className={cx("group/row relative outline-focus-ring after:pointer-events-none hover:bg-secondary focus-visible:outline-2 focus-visible:-outline-offset-2", "h-18 text-sm font-semibold text-primary", "[&>td]:after:absolute [&>td]:after:inset-x-0 [&>td]:after:bottom-0 [&>td]:after:h-px [&>td]:after:w-full [&>td]:after:bg-border-secondary last:[&>td]:after:hidden focus-visible:[&>td]:after:opacity-0", isUnfavoriting_0 && "opacity-0")} style={isUnfavoriting_0 ? {
                transition: "opacity 800ms"
              } : {
                transition: "background-color 150ms"
              }}>
													{row_5.getVisibleCells().map(cell => {
                  const isHiddenCell = !isLoggedIn && !hasAccessToPremiumFeatures && PREMIUM_BLUR_COLUMN_IDS.has(cell.column.id || "");
                  const isSortedCell = cell.column.id === activeSortedColumnId;
                  const isRightAlignedColumn = RIGHT_ALIGNED_COLUMN_IDS.has(cell.column.id || "");
                  return <td key={cell.id} data-column-id={cell.column.id} data-locked-column-start={cell.column.id === lockedColumnStartId ? "true" : undefined} className={cx("relative text-sm font-normal text-tertiary outline-focus-ring focus-visible:z-1 focus-visible:outline-2 focus-visible:-outline-offset-2", getTableCellHorizontalPadding(cell.column.id || ""), cell.column.id === "favorite" && "min-w-10 shrink-0", isRightAlignedColumn && "text-right", TABULAR_NUMS_COLUMN_IDS.has(cell.column.id || "") && "tabular-nums", isHiddenCell && "relative z-10", isSortedCell && SORTED_COLUMN_CELL_CLASS)}>
																{flexRender(cell.column.columnDef.cell, cell.getContext())}
															</td>;
                })}
												</tr>;
            });
          })()}
						</tbody>
					</table>

					{/* Promotional Panel - shown when user doesn't have premium features */}
					{shouldShowLockedInsightsGate && lockedPanelMetrics && <div className="absolute z-20 overflow-x-hidden" style={{
        top: `${lockedPanelMetrics.top}px`,
        left: `${lockedPanelMetrics.left}px`,
        height: `${lockedPanelMetrics.height}px`,
        width: `${lockedPanelMetrics.width}px`
      }}>
							<LockedInsightsGate isLoggedIn={isLoggedIn} />
						</div>}
				</div>}

			<PaginationCardDefault page={table.state.pagination.pageIndex + 1} total={table.getPageCount()} onPageChange={handlePaginationPageChange} className="px-4 py-3 md:px-6 md:pt-3 md:pb-4" />
		</TableCard.Root>;
};
export default function PoolsTableTanstack(props) {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Table {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}